
import React, { useState } from 'react';
import { Icons } from '../constants';
import ArbE from './ArbE';

interface Step {
  title: string;
  description: string;
  target: string;
  icon: any;
}

const STEPS: Step[] = [
  {
    title: "Your Project Hub",
    description: "Track all your tree care services, approve quotes, and pay invoices securely from one central location.",
    target: "Projects",
    icon: Icons.Calendar
  },
  {
    title: "Interactive Site Planner",
    description: "Map your property! Pin individual trees, upload photos for AI health analysis, and keep a digital history of every sapling.",
    target: "Property Planner",
    icon: Icons.Tree
  },
  {
    title: "24/7 AI Arborist",
    description: "Ask questions about local Columbus tree laws, pruning best practices, or storm season preparation anytime.",
    target: "AI Advisor",
    icon: Icons.Safety
  }
];

interface Props {
  onComplete: () => void;
}

const OnboardingTour: React.FC<Props> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  if (isMinimized) {
    return (
      <button 
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-8 right-8 bg-palm text-white px-6 py-3 rounded-full shadow-2xl flex items-center space-x-2 z-[9999] hover:bg-gecko transition"
      >
        <Icons.Safety />
        <span className="font-bold">Resume Hub Tour</span>
      </button>
    );
  }

  const step = STEPS[currentStep];

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center p-6 bg-bark/20 backdrop-blur-[2px]">
      <div className="max-w-md w-full bg-white rounded-[32px] shadow-2xl p-8 border border-stone-100 pointer-events-auto transform animate-fade-in">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center space-x-4">
            <ArbE className="w-16 h-16" />
            <div className="w-12 h-12 bg-leaf/20 text-palm rounded-xl flex items-center justify-center">
              <step.icon />
            </div>
          </div>
          <button onClick={() => setIsMinimized(true)} className="text-stone-400 hover:text-bark">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <h3 className="text-2xl font-bold text-bark mb-3">{step.title}</h3>
        <p className="text-stone-500 leading-relaxed mb-8">
          {step.description}
        </p>

        <div className="flex items-center justify-between">
          <div className="flex space-x-1">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 rounded-full transition-all ${i === currentStep ? 'w-8 bg-gecko' : 'w-2 bg-stone-100'}`} />
            ))}
          </div>
          
          <div className="flex space-x-3">
            <button 
              onClick={onComplete}
              className="px-4 py-2 text-stone-400 font-bold hover:bg-stone-50 rounded-xl"
            >
              Skip
            </button>
            <button 
              onClick={() => {
                if (currentStep < STEPS.length - 1) {
                  setCurrentStep(currentStep + 1);
                } else {
                  onComplete();
                }
              }}
              className="px-6 py-2 bg-palm text-white font-bold rounded-xl hover:bg-gecko transition"
            >
              {currentStep === STEPS.length - 1 ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
